// FILE: src/components/QuestionFactory/QuestionFactoryScreen.js
//
// Question Factory admin screen.
//
// Phase 2a (this PR):
//   • Generation form — admin picks cert + provider + count + difficulty,
//     submits, the question-factory-generate edge function persists drafts
//     into pipeline_items via add_pipeline_item.
//   • Click a run row → run detail view (items + audit trail).
//   • Smoke-test button retained for quick schema verification.
//
// Phase 2b adds Claude + PMI Infinity to the provider dropdown.
// Phase 2c adds validation + adjudication.

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  createPipelineRun,
  listPipelineRuns,
  listAvailableProviders,
  getDashboard,
} from '../../services/questionFactoryService';
import GenerationForm from './GenerationForm';
import CaseFactoryPanel from './CaseFactoryPanel';
import PipelineRunDetail from './PipelineRunDetail';
import AutoPublishSettings from './AutoPublishSettings';
import GenerationPanel from './GenerationPanel';

const STYLES = {
  wrapper: {
    minHeight: '100vh',
    background: '#0f0f1e',
    color: '#eee',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: '32px',
    boxSizing: 'border-box',
  },
  header: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' },
  back: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #555',
    borderRadius: '4px',
    color: '#ccc',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  title: { fontSize: '1.4rem', fontWeight: 700, letterSpacing: '0.4px', flex: 1 },
  badge: {
    fontSize: '0.7rem',
    padding: '3px 10px',
    background: '#2c2c4a',
    borderRadius: '999px',
    border: '1px solid #5050aa',
    color: '#aab',
  },
  banner: {
    background: '#1a1a2e',
    border: '1px solid #303060',
    borderRadius: '8px',
    padding: '16px 20px',
    marginBottom: '24px',
    fontSize: '0.9rem',
    lineHeight: 1.5,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  card: {
    background: '#15152a',
    border: '1px solid #2a2a4a',
    borderRadius: '8px',
    padding: '16px',
  },
  metric: { fontSize: '1.6rem', fontWeight: 700 },
  label: { fontSize: '0.75rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.4px' },
  sectionTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#aab',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginTop: '24px',
    marginBottom: '12px',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #2a2a4a', color: '#aab', fontWeight: 600 },
  td: { padding: '8px 10px', borderBottom: '1px solid #20203a', color: '#ddd' },
  rowClickable: { cursor: 'pointer' },
  empty: { padding: '16px', color: '#888', fontStyle: 'italic' },
  button: {
    padding: '8px 16px',
    background: '#3a3a6e',
    border: '1px solid #5a5a9e',
    borderRadius: '6px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  error: { color: '#ff6b6b', fontSize: '0.85rem', marginTop: '8px' },
};

function fmt(value, fallback = '—') {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value);
}

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function QuestionFactoryScreen({ onBack, onOpenInEditor, onQuestionStoreInvalidated }) {
  const [runs, setRuns]         = useState([]);
  const [dashboard, setDash]    = useState({});
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [creating, setCreating] = useState(false);
  const [activeRunId, setActiveRunId] = useState(null);

  const providers = useMemo(() => listAvailableProviders(), []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, d] = await Promise.all([listPipelineRuns({ limit: 25 }), getDashboard()]);
      setRuns(r);
      setDash(d);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleSmokeTest = useCallback(async () => {
    setCreating(true);
    setError(null);
    try {
      await createPipelineRun({
        examId:         'pmp',
        source:         'manual_import',
        domain:         'smoke-test',
        requestedCount: 0,
        options:        { phase: 1, smoke_test: true },
      });
      await reload();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setCreating(false);
    }
  }, [reload]);

  const handleRunCreated = useCallback(async (runId) => {
    await reload();
    setActiveRunId(runId);
  }, [reload]);

  // If a run is active, show its detail view.
  if (activeRunId) {
    return (
      <div data-testid="question-factory-screen" style={STYLES.wrapper}>
        <div style={STYLES.header}>
          <button data-testid="qf-back" style={STYLES.back} onClick={onBack}>← Admin</button>
          <span style={STYLES.title}>🏭 Question Factory</span>
          <span style={STYLES.badge}>Phase 3 · Publish</span>
        </div>
        <PipelineRunDetail runId={activeRunId} onBack={() => setActiveRunId(null)} onOpenInEditor={onOpenInEditor} onQuestionStoreInvalidated={onQuestionStoreInvalidated} />
      </div>
    );
  }

  return (
    <div data-testid="question-factory-screen" style={STYLES.wrapper}>
      <div style={STYLES.header}>
        <button data-testid="qf-back" style={STYLES.back} onClick={onBack}>← Admin</button>
        <span style={STYLES.title}>🏭 Question Factory</span>
        <span style={STYLES.badge}>Phase 3 · Publish</span>
      </div>

      <div style={STYLES.banner}>
        <strong>Phase 3 is live.</strong> Generation chains into validation +
        multi-AI rubric adjudication, then drafts surface in the staging queue with
        publish recommendations. Click a run row to Approve / Reject / Edit individual
        items or bulk-publish all auto-eligible ones. Per-cert auto-publish toggles
        below can elevate <code>auto_publish</code> recommendations straight into the
        live <code>questions</code> table (default OFF — admin approves by default).
        <br />
        <strong>Generation</strong> is controlled separately from publishing: the
        panel below shows, per certification, whether the overnight generator is
        allowed to run and exactly why it is or is not eligible. Auto-publish
        governs what happens to drafts; it does not start or stop generation.
      </div>

      <GenerationForm onRunCreated={handleRunCreated} />

      <CaseFactoryPanel />

      <GenerationPanel />

      <AutoPublishSettings />

      <div style={STYLES.grid}>
        <div style={STYLES.card}>
          <div style={STYLES.label}>Generated today</div>
          <div style={STYLES.metric}>{fmt(dashboard.generated_today, 0)}</div>
        </div>
        <div style={STYLES.card}>
          <div style={STYLES.label}>Published today</div>
          <div style={STYLES.metric}>{fmt(dashboard.published_today, 0)}</div>
        </div>
        <div style={STYLES.card}>
          <div style={STYLES.label}>Pending review</div>
          <div style={STYLES.metric}>{fmt(dashboard.pending_review, 0)}</div>
        </div>
        <div style={STYLES.card}>
          <div style={STYLES.label}>Avg validation score</div>
          <div style={STYLES.metric}>{fmt(dashboard.avg_validation_score, '—')}</div>
        </div>
      </div>

      <div style={STYLES.sectionTitle}>Providers</div>
      <table style={STYLES.table}>
        <thead>
          <tr>
            <th style={STYLES.th}>Provider</th>
            <th style={STYLES.th}>Modes</th>
            <th style={STYLES.th}>Default model</th>
            <th style={STYLES.th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {providers.map((p) => (
            <tr key={p.id} data-testid={`qf-provider-${p.id}`}>
              <td style={STYLES.td}>{p.displayName}</td>
              <td style={STYLES.td}>{p.supportedModes.join(', ') || '—'}</td>
              <td style={STYLES.td}>{fmt(p.defaultModel)}</td>
              <td style={STYLES.td}>
                {p.enabled
                  ? 'Enabled'
                  : p.cliOnly ? 'CLI script only'
                  : p.comingSoon ? 'Coming soon'
                  : 'Phase 2c'}
                {p.cliOnly && p.cliNote && (
                  <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '4px' }}>{p.cliNote}</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={STYLES.sectionTitle}>Pipeline Runs</div>
      <button
        data-testid="qf-smoke-test"
        style={STYLES.button}
        onClick={handleSmokeTest}
        disabled={creating}
      >
        {creating ? 'Creating…' : 'Create empty smoke-test run'}
      </button>
      {error && <div style={STYLES.error}>{error}</div>}

      <table style={{ ...STYLES.table, marginTop: '16px' }}>
        <thead>
          <tr>
            <th style={STYLES.th}>Created</th>
            <th style={STYLES.th}>Exam</th>
            <th style={STYLES.th}>Source</th>
            <th style={STYLES.th}>Model</th>
            <th style={STYLES.th}>Status</th>
            <th style={STYLES.th} title="Stage 1">Gen</th>
            <th style={STYLES.th} title="Stage 2 — structural validation">Val</th>
            <th style={STYLES.th} title="Stage 3 — multi-AI rubric adjudication">Adj</th>
            <th style={STYLES.th} title="Items at publish_recommendation = auto_publish (overall quality 90+)">Auto</th>
            <th style={STYLES.th} title="Items rejected by adjudication (overall quality < 70)">Rej</th>
            <th style={STYLES.th}>Staged</th>
            <th style={STYLES.th}>Published</th>
            <th style={STYLES.th}>Est. cost</th>
            <th style={STYLES.th}>Created by</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td style={STYLES.empty} colSpan={14}>Loading…</td></tr>
          )}
          {!loading && runs.length === 0 && (
            <tr><td style={STYLES.empty} colSpan={14}>
              No pipeline runs yet. Use the form above to generate a batch.
            </td></tr>
          )}
          {runs.map((r) => (
            <tr key={r.id} data-testid={`qf-run-${r.id}`}
                style={STYLES.rowClickable}
                onClick={() => setActiveRunId(r.id)}>
              <td style={STYLES.td}>{formatDate(r.created_at)}</td>
              <td style={STYLES.td}>{r.exam_id}</td>
              <td style={STYLES.td}>{r.source}</td>
              <td style={STYLES.td}>{fmt(r.provider_model, '—')}</td>
              <td style={STYLES.td}>{r.status}</td>
              <td style={STYLES.td}>{r.generated_count}</td>
              <td style={STYLES.td}>{r.validated_count}</td>
              <td style={STYLES.td}>{r.adjudicated_count}</td>
              <td style={STYLES.td}>
                {r.auto_publish_eligible_count > 0 ? (
                  <span style={{ color: '#7be88c', fontWeight: 600 }}>
                    {r.auto_publish_eligible_count}
                  </span>
                ) : '0'}
              </td>
              <td style={STYLES.td}>
                {r.rejected_count > 0 ? (
                  <span style={{ color: '#ff9b9b', fontWeight: 600 }}>{r.rejected_count}</span>
                ) : '0'}
              </td>
              <td style={STYLES.td}>{r.staged_count}</td>
              <td style={STYLES.td}>{r.published_count}</td>
              <td style={STYLES.td}>
                {r.estimated_cost_usd != null && r.estimated_cost_usd > 0
                  ? `$${Number(r.estimated_cost_usd).toFixed(4)}`
                  : '—'}
              </td>
              <td style={STYLES.td}>{r.created_by}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
